import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useWallet } from '../components/WalletContext';
import { web } from '@klever/sdk-web';
import axios from 'axios';
import {
  FaTag, FaShoppingCart, FaExchangeAlt, FaGift,
  FaArrowLeft, FaExternalLinkAlt, FaCopy, FaCheckCircle,
  FaClock, FaUser, FaChartLine, FaHistory, FaInfoCircle,
  FaShare, FaFire, FaTimesCircle, FaGavel, FaCube, FaMinus, FaPlus
} from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import AuctionListingModal from '../components/AuctionListingModal';
import { isSFT, getSFTBalance, validateSFTTransfer } from '../utils/sftUtils';
import '../styles/NftDetailsPage.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const NftDetailsPage = () => {
  const { isConnected, walletAddress, useKdaFee, selectedKda } = useWallet();
  const navigate = useNavigate();
  const { collectionId, nftId } = useParams();
  
  // State management
  const [nft, setNft] = useState(null);
  const [collection, setCollection] = useState(null);
  const [collectionLogo, setCollectionLogo] = useState(null);
  const [history, setHistory] = useState([]);
  const [nftOwner, setNftOwner] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [listingPrice, setListingPrice] = useState("");
  const [isPriceConfirmed, setIsPriceConfirmed] = useState(false);
  const [latestPrice, setLatestPrice] = useState(null);
  const [bidAmount, setBidAmount] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [showAuctionModal, setShowAuctionModal] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState("");
  const [endDate, setEndDate] = useState(new Date().toISOString().substring(0, 10));
  const [endTimeUnix, setEndTimeUnix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState({ sales: 0, avgPrice: 0, volume: 0 });
  const [salesChartData, setSalesChartData] = useState(null);

  // SFT-specific state
  const [buyQuantity, setBuyQuantity] = useState(1);
  const [availableQuantity, setAvailableQuantity] = useState(null);
  const [assetDetails, setAssetDetails] = useState(null);

  // Auction-specific state
  const [isAuction, setIsAuction] = useState(false);
  const [reservePrice, setReservePrice] = useState(null);
  const [bids, setBids] = useState([]);
  const [highestBid, setHighestBid] = useState(null);
  const [userBid, setUserBid] = useState(null); // Track if user has bid

  const ExchangeAddress = "klv1qqqqqqqqqqqqqqqpqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqylllsymmgky";
  const priceInputRef = useRef();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(19,19,27,0.9)',
        titleColor: '#fff',
        bodyColor: '#b8b8d4',
        borderColor: 'rgba(99,102,241,0.2)',
        borderWidth: 1,
        mode: 'index',
        intersect: false,
      }
    },
    scales: {
      x: {
        grid: { color: 'rgba(255,255,255,0.05)' },
        ticks: { color: '#7d7d9a' },
      },
      y: {
        grid: { color: 'rgba(255,255,255,0.05)' },
        ticks: { color: '#7d7d9a' },
      }
    },
    elements: {
      point: {
        radius: 3,
        hoverRadius: 5,
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  // Transaction functions
  const listNftForSale = useCallback(async () => {
    if (!listingPrice || !endTimeUnix) {
      setTransactionStatus("error");
      return false;
    }

    const payload = {
      marketType: 0,
      marketplaceId: "417b70c0eb7a33cb",
      assetId: `${collectionId}/${nftId}`,
      currencyId: "KLV",
      price: Math.floor(parseFloat(listingPrice) * 10 ** 6),
      endTime: endTimeUnix,
    };

    let txOptions = undefined;
    if (useKdaFee && selectedKda) txOptions = { kdaFee: selectedKda };

    try {
      const unsignedTx = await web.buildTransaction([{ payload, type: 18 }], undefined, txOptions);
      const signedTx = await web.signTransaction(unsignedTx);
      const response = await web.broadcastTransactions([signedTx]);
      console.log('Transaction response:', response);
      setTransactionStatus("success");
      return true;
    } catch (error) {
      console.error('Error sending transaction:', error);
      setTransactionStatus("error");
      return false;
    }
  }, [collectionId, nftId, listingPrice, endTimeUnix, useKdaFee, selectedKda]);

  const claimNft = useCallback(async () => {
    const payload = { claimType: 2, id: orderId };
    const contract = { type: 9, payload };

    let txOptions = undefined;
    if (useKdaFee && selectedKda) txOptions = { kdaFee: selectedKda };

    try {
      const unsignedTx = await web.buildTransaction([contract], undefined, txOptions);
      const signedTx = await web.signTransaction(unsignedTx);
      const response = await web.broadcastTransactions([signedTx]);
      console.log('Claim transaction response:', response);
      setTransactionStatus("success");
      return true;
    } catch (error) {
      console.error('Error in claim transaction:', error);
      setTransactionStatus("error");
      return false;
    }
  }, [orderId, useKdaFee, selectedKda]);

  const buyNft = useCallback(async (buyOrderId, price, quantity = 1) => {
    const totalAmount = Math.floor(price * quantity * 10 ** 6);
    const payload = {
      buyType: 1,
      id: buyOrderId,
      currencyId: "KLV",
      amount: totalAmount
    };

    // Add quantity field for SFTs
    if (assetDetails?.isSFT && quantity > 1) {
      payload.amount = quantity;
    }

    const contract = { type: 17, payload };

    let txOptions = undefined;
    if (useKdaFee && selectedKda) txOptions = { kdaFee: selectedKda };

    try {
      const unsignedTx = await web.buildTransaction([contract], undefined, txOptions);
      const signedTx = await web.signTransaction(unsignedTx);
      const response = await web.broadcastTransactions([signedTx]);
      console.log('Buy NFT transaction response:', response);
      setTransactionStatus("success");
      return true;
    } catch (error) {
      console.error('Error in buy NFT transaction:', error);
      setTransactionStatus("error");
      return false;
    }
  }, [useKdaFee, selectedKda, assetDetails]);

  const cancelOrder = useCallback(async () => {
    const payload = { orderType: 0, orderId };
    const contract = { type: 19, payload };

    let txOptions = undefined;
    if (useKdaFee && selectedKda) txOptions = { kdaFee: selectedKda };

    try {
      const unsignedTx = await web.buildTransaction([contract], undefined, txOptions);
      const signedTx = await web.signTransaction(unsignedTx);
      const response = await web.broadcastTransactions([signedTx]);
      console.log('Cancel order transaction response:', response);
      setTransactionStatus("success");
      return true;
    } catch (error) {
      console.error('Error in cancel order transaction:', error);
      setTransactionStatus("error");
      return false;
    }
  }, [orderId, useKdaFee, selectedKda]);

  // Place a bid on auction
  const placeBid = useCallback(async (bidAmount) => {
    if (!orderId || !bidAmount) {
      setTransactionStatus("error");
      return false;
    }

    const payload = {
      buyType: 1, // MarketBuy (bid)
      id: orderId,
      currencyId: "KLV",
      amount: Math.floor(bidAmount * 10 ** 6)
    };

    let txOptions = undefined;
    if (useKdaFee && selectedKda) txOptions = { kdaFee: selectedKda };

    try {
      const unsignedTx = await web.buildTransaction([{ payload, type: 17 }], undefined, txOptions);
      const signedTx = await web.signTransaction(unsignedTx);

      if (!signedTx) {
        setTransactionStatus("error");
        return false;
      }

      await web.broadcastTransactions([signedTx]);
      setTransactionStatus("success");

      // Refresh bids after placing bid
      setTimeout(() => {
        fetchBids(orderId);
      }, 2000);

      return true;
    } catch (error) {
      console.error('Error placing bid:', error);
      setTransactionStatus("error");
      return false;
    }
  }, [orderId, useKdaFee, selectedKda]);

  // Data fetching
  const checkNftOwnership = async () => {
    try {
      const response = await axios.get(
        `https://api.mainnet.klever.org/v1.0/assets/nft/holder/${encodeURIComponent(collectionId)}/${encodeURIComponent(nftId)}`
      );
      if (response.data?.data?.account?.address) {
        setNftOwner(response.data.data.account.address);
      }
    } catch (error) {
      console.error('Error checking NFT ownership:', error);
    }
  };

  const fetchCollectionData = async () => {
    try {
      const [page1, page2] = await Promise.all([
        axios.get('https://api.mainnet.klever.org/v1.0/assets/list?page=1&limit=100'),
        axios.get('https://api.mainnet.klever.org/v1.0/assets/list?page=2&limit=100')
      ]);
      
      const assets = [...page1.data.data.assets, ...page2.data.data.assets];
      const matchingAsset = assets.find(asset => asset.assetId === collectionId);
      
      if (matchingAsset) {
        setCollection(matchingAsset);
        const logoUriObj = matchingAsset.uris?.find(uri => uri.key.toLowerCase() === 'logo');
        let logoUri = logoUriObj?.value || null;
        if (logoUri) {
          if (logoUri.includes("klever-mint.mypinata.cloud")) {
            logoUri = logoUri.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
          } else if (!logoUri.startsWith('http')) {
            logoUri = `https://ipfs.io/ipfs/${logoUri}`;
          }
          setCollectionLogo(logoUri);
        }
        return matchingAsset;
      }
      return null;
    } catch (error) {
      console.error('Error fetching collection:', error);
      return null;
    }
  };

  const fetchMetadata = async (collectionData) => {
    try {
      if (!collectionData) {
        throw new Error('Collection data not found');
      }

      const metadataObject = collectionData.uris?.find(uri => uri.key.toLowerCase() === 'metadata');
      let metadataUri = metadataObject?.value || null;

      if (!metadataUri) {
        throw new Error('Metadata URI not found');
      }

      if (metadataUri === "QmNaa2KQ6NkjjESpPHEnAow9hivnsAkq2Gd6R26cHG28Er") {
        metadataUri = `https://ipfs.io/ipfs/${metadataUri}/${nftId}.json`;
      } else {
        if (metadataUri.includes("klever-mint.mypinata.cloud")) {
          metadataUri = metadataUri.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
        }
        metadataUri = `${metadataUri}/${nftId}`;
      }

      try {
        const metadataResponse = await axios.get(metadataUri);
        const metadata = metadataResponse.data;

        if (metadata.image) {
          if (metadata.image.includes("www.lovemonsternft.com")) {
            const imageName = metadata.image.split('/').pop();
            metadata.image = `https://ipfs.io/ipfs/QmWVmUDPBeQzv6fG93JxQxFVee8b6smFD3RQosQXJHiZTJ/${imageName}`;
          } else if (metadata.image.includes("klever-mint.mypinata.cloud")) {
            metadata.image = metadata.image.replace("https://klever-mint.mypinata.cloud", "https://ipfs.io");
          }
        }

        setNft(metadata);
      } catch (primaryError) {
        console.error('Primary fetch failed, trying fallback');
        const fallbackUri = `https://${metadataUri}.ipfs.w3s.link/${nftId}`;
        const fallbackResponse = await axios.get(fallbackUri);
        setNft(fallbackResponse.data);
      }
    } catch (error) {
      console.error(`Error fetching metadata:`, error);
      throw error;
    }
  };

  const calculateStats = (historyData) => {
    let sales = 0;
    let totalVolume = 0;
    let salesData = [];

    historyData.forEach(item => {
      const buyContract = item.contract?.find(c => 
        c.typeString === "BuyContractType" && c.parameter?.buyType === "MarketBuy"
      );
      if (buyContract && buyContract.parameter?.amount) {
        sales++;
        const amount = buyContract.parameter.amount / 1000000;
        totalVolume += amount;
        salesData.push({
          date: new Date(item.timestamp * 1000).toLocaleDateString('en-US', {month: 'short', day: 'numeric'}),
          price: amount
        });
      }
    });

    const avgPrice = sales > 0 ? totalVolume / sales : 0;

    setStats({
      sales,
      avgPrice: avgPrice.toFixed(2),
      volume: totalVolume.toFixed(2)
    });

    if (salesData.length > 0) {
      const grouped = salesData.reduce((acc, curr) => {
        acc[curr.date] = (acc[curr.date] || 0) + curr.price;
        return acc;
      }, {});

      const labels = Object.keys(grouped).sort((a, b) => new Date(a) - new Date(b));
      const data = labels.map(label => grouped[label]);

      setSalesChartData({
        labels,
        datasets: [{
          label: 'Sales Volume (KLV)',
          data,
          borderColor: '#6366f1',
          backgroundColor: 'rgba(99, 102, 241, 0.2)',
          fill: true,
          tension: 0.4,
          pointBackgroundColor: '#8b5cf6',
          pointBorderColor: '#fff',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: '#6366f1'
        }]
      });
    } else {
      setSalesChartData(null);
    }
  };

  // Fetch bids for an auction
  const fetchBids = async (auctionOrderId) => {
    try {
      const response = await axios.get(
        `https://api.mainnet.klever.org/v1.0/transaction/list?limit=100&type=17&status=success&orderid=${auctionOrderId}`
      );

      const transactions = response.data.data.transactions || [];

      const auctionBids = transactions
        .filter(tx => {
          const buyContract = tx.contract?.find(c => c.type === 17 || c.typeString === 'BuyContractType');
          const matchesOrder = buyContract?.parameter?.id === auctionOrderId;
          const isMarketBuy = buyContract?.parameter?.buyType === 'MarketBuy' || buyContract?.parameter?.buyType === 1;
          return matchesOrder && isMarketBuy;
        })
        .map(tx => {
          const buyContract = tx.contract.find(c => c.type === 17 || c.typeString === 'BuyContractType');
          return {
            bidder: tx.sender,
            amount: (buyContract.parameter.amount || 0) / 1000000,
            timestamp: tx.timestamp,
            hash: tx.hash
          };
        })
        .sort((a, b) => b.amount - a.amount);

      setBids(auctionBids);
      if (auctionBids.length > 0) {
        setHighestBid(auctionBids[0]);
      }

      const userBidEntry = auctionBids.find(bid => bid.bidder === walletAddress);
      setUserBid(userBidEntry);
    } catch (error) {
      console.error('Error fetching bids:', error);
      setBids([]);
    }
  };

  const fetchNftData = async () => {
    try {
      setLoading(true);
      setError(null);

      const collectionData = await fetchCollectionData();
      await fetchMetadata(collectionData);

      // Fetch full asset details to check if it's an SFT
      try {
        const assetId = `${collectionId}/${nftId}`;
        const assetResponse = await axios.get(
          `https://api.mainnet.klever.org/v1.0/assets/${assetId}`
        );
        const fullAssetData = assetResponse.data?.data?.asset;

        if (fullAssetData) {
          const isSftAsset = isSFT(fullAssetData);
          setAssetDetails({
            ...fullAssetData,
            isSFT: isSftAsset
          });

          // If it's an SFT, fetch available quantity from marketplace listing
          if (isSftAsset) {
            // Available quantity will be determined from the sell contract later
            setAvailableQuantity(fullAssetData.maxSupply || 1);
          }
        }
      } catch (assetError) {
        console.error('Error fetching asset details:', assetError);
      }

      let allHistory = [];
      const totalPages = 5;

      for (let page = 1; page <= totalPages; page++) {
        const url = `https://api.mainnet.klever.org/marketplaces/nfts/activity?nftId=${collectionId}/${nftId}&sort=desc&limit=10&page=${page}`;
        try {
          const response = await axios.get(url);
          if (response.data?.data?.history) {
            allHistory = allHistory.concat(response.data.data.history);
          }
        } catch (error) {
          console.error(`Error fetching history page ${page}:`, error);
        }
      }

      setHistory(allHistory);
      calculateStats(allHistory);

      if (allHistory.length > 0) {
        const latestTransaction = allHistory[0];
        const assetId = `${collectionId}/${nftId}`;
        const sellContract = latestTransaction.contract?.find(
          c => c.typeString === "SellContractType" && c.parameter?.assetId === assetId
        );

        if (sellContract) {
          setEndTime(sellContract.parameter.endTime);

          // Check if it's an auction (marketType 1 or "AuctionMarket" string)
          const marketType = sellContract.parameter.marketType;
          const isAuctionListing = marketType === 1 || marketType === "AuctionMarket";
          setIsAuction(isAuctionListing);

          // Get reserve price for auctions (this is the "buy now" price)
          if (isAuctionListing && sellContract.parameter.reservePrice) {
            const reserve = sellContract.parameter.reservePrice / 1000000;
            setReservePrice(reserve);
          }

          // Get starting bid price for auctions
          if (isAuctionListing && sellContract.parameter.price) {
            const startPrice = sellContract.parameter.price / 1000000;
            setLatestPrice(startPrice);
          }

          // Get available quantity for SFTs
          if (sellContract.parameter.amount) {
            setAvailableQuantity(sellContract.parameter.amount);
            setBuyQuantity(1); // Reset to 1
          }

          let foundOrderId = null;

          latestTransaction.receipts?.forEach(receipt => {
            if (receipt.assetId === assetId && receipt.typeString === "KAppTransfer") {
              foundOrderId = receipt.orderId;
              setOrderId(receipt.orderId);
            }
          });

          // If orderId still not found, fetch the full transaction to get receipts
          if (!foundOrderId && latestTransaction.hash) {
            axios.get(`https://api.mainnet.klever.org/v1.0/transaction/${latestTransaction.hash}`)
              .then(txResponse => {
                const fullTx = txResponse.data.data.transaction;
                fullTx.receipts?.forEach(receipt => {
                  if (receipt.typeString === "Sell" && receipt.orderId) {
                    foundOrderId = receipt.orderId;
                    setOrderId(receipt.orderId);

                    if (isAuctionListing) {
                      fetchBids(receipt.orderId);
                    }
                  }
                });
              })
              .catch(err => console.error('Error fetching full transaction:', err));
          } else if (isAuctionListing && foundOrderId) {
            fetchBids(foundOrderId);
          }
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching NFT data:', error);
      setError(error.message || 'Failed to load NFT data');
      setLoading(false);
    }
  };

  const checkLatestTransaction = useCallback(() => {
    if (!history || history.length === 0) {
      return { isSale: false, price: null, sender: null };
    }

    const latest = history[0];
    const isSale = latest.contract?.some(c => c.typeString === "SellContractType");

    if (isSale) {
      const sale = latest.contract.find(c => c.typeString === "SellContractType");
      const price = sale?.parameter?.price ? sale.parameter.price / 1000000 : null;
      const sender = latest.sender;
      return { isSale, price, sender };
    }

    return { isSale: false, price: null, sender: null };
  }, [history]);

  useEffect(() => {
    if (history?.length) {
      const { isSale, price } = checkLatestTransaction();
      if (isSale && price) {
        setLatestPrice(price);
      }
    }
  }, [history, checkLatestTransaction]);

  useEffect(() => {
    fetchNftData();
    checkNftOwnership();
  }, [collectionId, nftId]);

  const goBack = () => navigate(-1);

  const copyToClipboard = (text, type = 'NFT ID') => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareNFT = () => {
    if (navigator.share) {
      navigator.share({
        title: nft?.name || `NFT #${nftId}`,
        text: `Check out this NFT from ${collectionId}`,
        url: window.location.href
      });
    } else {
      copyToClipboard(window.location.href, 'Link');
    }
  };

  const openModal = (title) => {
    setModalTitle(title);
    setIsModalOpen(true);
    setTransactionStatus("");
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalTitle("");
    setTransactionStatus("");
    setIsPriceConfirmed(false);
    setListingPrice("");
  };

  const { isSale, sender } = checkLatestTransaction();

  const handleBuy = () => openModal("Buy NFT");
  const handleCancel = () => openModal("Cancel Listing");
  const handleSell = () => setShowAuctionModal(true);
  const handleClaim = () => openModal("Claim NFT");
  const handleClaimRefund = () => openModal("Claim Refund");

  const handleTransactionConfirmation = useCallback(async () => {
    setTransactionStatus("processing");

    let success = false;
    if (modalTitle.includes('Sell')) {
      if (!listingPrice || parseFloat(listingPrice) <= 0) {
        setTransactionStatus("error");
        return;
      }
      if (!endTimeUnix) {
        setTransactionStatus("error");
        return;
      }
      success = await listNftForSale();
    } else if (modalTitle.includes('Claim')) {
      success = await claimNft();
    } else if (modalTitle.includes('Place Bid')) {
      if (!bidAmount || parseFloat(bidAmount) <= 0) {
        setTransactionStatus("error");
        return;
      }
      const minBid = highestBid ? highestBid.amount + 0.01 : (reservePrice || 0);
      if (parseFloat(bidAmount) < minBid) {
        setTransactionStatus("error");
        return;
      }
      success = await placeBid(parseFloat(bidAmount));
    } else if (modalTitle.includes('Buy')) {
      // Validate SFT quantity if applicable
      if (assetDetails?.isSFT) {
        const validation = validateSFTTransfer(buyQuantity, availableQuantity || 1, availableQuantity || 1);
        if (!validation.valid) {
          setTransactionStatus("error");
          return;
        }
      }
      success = await buyNft(orderId, latestPrice, buyQuantity);
    } else if (modalTitle.includes('Cancel')) {
      success = await cancelOrder();
    } else if (modalTitle.includes('Refund')) {
      success = await claimNft(); // Use same claim for refund
    }
    
    if (success) {
      setTimeout(() => {
        closeModal();
        fetchNftData();
        checkNftOwnership();
      }, 2000);
    }
  }, [modalTitle, listNftForSale, claimNft, buyNft, cancelOrder, placeBid, orderId, latestPrice, bidAmount]);

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setEndDate(newDate);
    const timestamp = new Date(newDate + 'T23:59:59').getTime();
    setEndTimeUnix(Math.floor(timestamp / 1000));
  };

  const confirmPrice = () => {
    const newPrice = priceInputRef.current?.value;
    if (newPrice && !isNaN(newPrice) && parseFloat(newPrice) > 0) {
      setListingPrice(newPrice);
      setIsPriceConfirmed(true);
    }
  };

  const handleBuyQuantityChange = (newQuantity) => {
    const maxQty = availableQuantity || 1;
    const qty = Math.max(1, Math.min(maxQty, parseInt(newQuantity) || 1));
    setBuyQuantity(qty);
  };

  const isAuctionEnded = () => endTime && Date.now() / 1000 > endTime;
  const isClaimableForSeller = () => isAuctionEnded() && isSale && nftOwner === ExchangeAddress && sender === walletAddress && bids.length === 0;

  const isWinner = highestBid?.bidder === walletAddress;
  const hasBidButNotWinner = userBid && !isWinner;

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', { 
      dateStyle: 'medium', 
      timeStyle: 'short' 
    });
  };

  const truncateAddress = (address) => {
    if (!address) return 'unknown';
    if (address.length <= 12) return address;
    return `${address.slice(0, 8)}...${address.slice(-4)}`;
  };

  const getHistoryIcon = (item) => {
    const sellContract = item.contract?.find(c => c.typeString === "SellContractType");
    const marketBuyContract = item.contract?.find(c => c.typeString === "BuyContractType" && c.parameter?.buyType === "MarketBuy");
    const cancelContract = item.contract?.find(c => c.typeString === "CancelMarketOrderContractType");
    const itoBuyContract = item.contract?.find(c => c.typeString === "BuyContractType" && c.parameter?.buyType === "ITOBuy");
    const assetTriggerContract = item.contract?.find(c => c.typeString === "AssetTriggerContractType" && c.parameter?.triggerType === "Mint");
    const claimContract = item.contract?.find(c => c.typeString === "ClaimContractType");
    const transferContract = item.contract?.find(c => c.typeString === "TransferContractType");

    // Check if sell is an auction
    if (sellContract) {
      const isAuctionType = sellContract.parameter?.marketType === 1 || sellContract.parameter?.marketType === "AuctionMarket";
      return { icon: isAuctionType ? FaGavel : FaTag, class: isAuctionType ? 'auction' : 'sell' };
    }
    if (marketBuyContract) return { icon: FaShoppingCart, class: 'buy' };
    if (cancelContract) return { icon: FaExchangeAlt, class: 'cancel' };
    if (itoBuyContract || assetTriggerContract) return { icon: FaGift, class: 'mint' };
    if (claimContract) return { icon: FaGift, class: 'claim' };
    if (transferContract) return { icon: FaExchangeAlt, class: 'transfer' };

    return { icon: FaInfoCircle, class: '' };
  };

  const getHistoryText = (item) => {
    const sellContract = item.contract?.find(c => c.typeString === "SellContractType");
    const marketBuyContract = item.contract?.find(c => c.typeString === "BuyContractType" && c.parameter?.buyType === "MarketBuy");
    const cancelContract = item.contract?.find(c => c.typeString === "CancelMarketOrderContractType");
    const itoBuyContract = item.contract?.find(c => c.typeString === "BuyContractType" && c.parameter?.buyType === "ITOBuy");
    const assetTriggerContract = item.contract?.find(c => c.typeString === "AssetTriggerContractType" && c.parameter?.triggerType === "Mint");
    const claimContract = item.contract?.find(c => c.typeString === "ClaimContractType");
    const transferContract = item.contract?.find(c => c.typeString === "TransferContractType");

    const who = item.sender === walletAddress ? <strong>You</strong> : truncateAddress(item.sender);

    if (sellContract) {
      const isAuctionType = sellContract.parameter?.marketType === 1 || sellContract.parameter?.marketType === "AuctionMarket";
      const price = (sellContract.parameter?.price / 1000000).toLocaleString('en-US');

      if (isAuctionType) {
        const startPrice = sellContract.parameter?.price ? (sellContract.parameter.price / 1000000).toLocaleString('en-US') : '0';
        const buyNow = sellContract.parameter?.reservePrice ? (sellContract.parameter.reservePrice / 1000000).toLocaleString('en-US') : null;
        return (
          <>
            {who} started auction at <strong>{startPrice} KLV</strong>
            {buyNow && <> (Buy Now: {buyNow} KLV)</>}
          </>
        );
      }
      return <>{who} listed for <strong>{price} KLV</strong></>;
    }
    if (marketBuyContract) {
      const price = (marketBuyContract.parameter?.amount / 1000000).toLocaleString('en-US');
      return <>{who} purchased for <strong>{price} KLV</strong></>;
    }
    if (cancelContract) return <>{who} cancelled the listing</>;
    if (itoBuyContract || assetTriggerContract) return <>{who} minted this NFT</>;
    if (claimContract) return <>{who} claimed the NFT</>;
    if (transferContract) {
      const to = truncateAddress(transferContract.parameter?.toAddress);
      return <>{who} transferred to <strong>{to}</strong></>;
    }

    return <>Unknown action</>;
  };

    if (loading) {
    return (
      <div className="nft-details-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p className="loading-text">Loading NFT data...</p>
        </div>
      </div>
    );
  }

  if (error || !nft) {
    return (
      <div className="nft-details-page">
        <div className="page-header">
          <button onClick={goBack} className="back-button">
            <FaArrowLeft /> <span>Back</span>
          </button>
        </div>
        <div className="loading-container">
          <h2>Error Loading NFT</h2>
          <p>{error || 'NFT not found'}</p>
          <button onClick={fetchNftData} className="action-button buy">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="nft-details-page">
      {/* Header */}
      <div className="page-header">
        <button onClick={goBack} className="back-button">
          <FaArrowLeft /> <span>Back</span>
        </button>
      </div>

      {/* Main Content - CRITICAL: This is the grid container */}
      <div className="nft-details-container">
        <div className="nft-content-grid">
          
          {/* LEFT COLUMN - Image & Stats */}
          <div className="nft-left-column">
            {/* Image Container with EXPLICIT sizing */}
            <div className="collection-nft-image-container">
              <div className="nft-image-frame">
                {!imageError && nft.image ? (
                  <img 
                    src={nft.image} 
                    alt={nft.name || `${collectionId} #${nftId}`}
                    className="nft-main-image"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <div className="nft-image-error">
                    <FaInfoCircle size={48} />
                    <p>Image unavailable</p>
                  </div>
                )}
                {isAuctionEnded() && (
                  <div className="nft-status-badge expired">AUCTION ENDED</div>
                )}
              </div>
            </div>

            {/* Collection Info */}
            {collection && (
              <div className="collection-info-card">
                <div className="collection-header">
                  {collectionLogo && !imageError && (
                    <img 
                      src={collectionLogo} 
                      alt={`${collection.name} logo`} 
                      className="details-collection-logo"
                      onError={() => setImageError(true)}
                    />
                  )}
                  <span className="collection-label">Collection</span>
                </div>
                <div className="collection-details">
                  <h4>{collection.name || collectionId}</h4>
                  {collection.maxSupply && (
                    <span className="collection-supply">
                      {collection.circulatingSupply || 0} / {collection.maxSupply} minted
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Stats Card */}
            <div className="nft-stats-section">
              <h3>Statistics</h3>
              <div className="stats-list">
                <div className="stat-row">
                  <div className="stat-info">
                    <FaFire className="stat-icon fire" />
                    <span className="stat-label">Total Sales</span>
                  </div>
                  <span className="stat-value">{stats.sales}</span>
                </div>
                <div className="stat-row">
                  <div className="stat-info">
                    <FaChartLine className="stat-icon chart" />
                    <span className="stat-label">Avg Price</span>
                  </div>
                  <span className="stat-value">{stats.avgPrice} KLV</span>
                </div>
                <div className="stat-row">
                  <div className="stat-info">
                    <FaShoppingCart className="stat-icon cart" />
                    <span className="stat-label">Volume</span>
                  </div>
                  <span className="stat-value">{stats.volume} KLV</span>
                </div>
              </div>
            </div>

            {/* Sales Chart Card */}
            <div className="sales-chart-card">
              <h3>Sales History</h3>
              <div className="chart-container">
                {salesChartData ? (
                  <Line data={salesChartData} options={chartOptions} />
                ) : (
                  <div className="no-data">
                    <FaChartLine size={32} />
                    <p>No sales data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN - Details */}
          <div className="nft-right-column">
            {/* Title & Actions */}
            <div className="nft-title-section">
              <h1 className="nft-name">{nft.name || `${collectionId} #${nftId}`}</h1>
              <div className="nft-quick-actions">
                <button className="details-quick-action-btn" onClick={shareNFT} title="Share">
                  <FaShare />
                </button>
                <button 
                  className="details-quick-action-btn" 
                  onClick={() => copyToClipboard(`${collectionId}/${nftId}`)}
                  title={copied === 'NFT ID' ? 'Copied!' : 'Copy ID'}
                >
                  {copied === 'NFT ID' ? <FaCheckCircle /> : <FaCopy />}
                </button>
                <a 
                  href={`https://kleverscan.org/nft/${collectionId}/${nftId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="details-quick-action-btn"
                  title="View on Explorer"
                >
                  <FaExternalLinkAlt />
                </a>
              </div>
            </div>

            <div className="nft-token-id">
              <span className="token-id-label">Token ID:</span>
              <span className="token-id-value">#{nftId}</span>
            </div>

            {/* Owner Card */}
            <div className="nft-owner-card">
              <div className="owner-main">
                <div className="owner-label-group">
                  <FaUser className="owner-icon" />
                  <span className="owner-label">Owner</span>
                </div>
                <button 
                  className="owner-address-btn"
                  onClick={() => copyToClipboard(nftOwner, 'Owner')}
                  title="Copy address"
                >
                  {nftOwner === walletAddress ? 'You' : truncateAddress(nftOwner)}
                  {copied === 'Owner' && <FaCheckCircle className="copied-icon" />}
                </button>
              </div>
              <div className="owner-badges">
                {isSale && nftOwner === ExchangeAddress && !isAuctionEnded() && (
                  <span className={`status-badge ${isAuction ? 'auction-badge' : 'listed'}`}>
                    {isAuction ? '🔨 On Auction' : 'For Sale'}
                  </span>
                )}
                {isSale && nftOwner === ExchangeAddress && isAuctionEnded() && (
                  <span className="status-badge expired-badge-inline">Expired</span>
                )}
                {nftOwner === walletAddress && !isSale && (
                  <span className="status-badge owned">You Own This</span>
                )}
              </div>
            </div>

            {/* Price Display */}
            {!isAuctionEnded() && latestPrice && (
              <div className="nft-price-card">
                <div className="price-main">
                  <span className="auction-price-label">
                    {isAuction ? (highestBid ? 'Highest Bid' : 'Reserve Price') : 'Current Price'}
                  </span>
                  <div className="price-display">
                    <span className="price-number">
                      {isAuction && highestBid
                        ? highestBid.amount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 6})
                        : (isAuction && reservePrice
                          ? reservePrice.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 6})
                          : latestPrice.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 6}))
                      }
                    </span>
                    <span className="price-token">KLV</span>
                  </div>
                  {isAuction && reservePrice && (
                    <span className="price-compare">
                      Acceptable price (Instant sell): {reservePrice.toLocaleString('en-US', {minimumFractionDigits: 2})} KLV
                    </span>
                  )}
                  {isAuction && bids.length > 0 && (
                    <span className="price-compare">
                      {bids.length} bid{bids.length !== 1 ? 's' : ''} placed
                    </span>
                  )}
                  {!isAuction && stats.avgPrice > 0 && (
                    <span className="price-compare">
                      {latestPrice > stats.avgPrice ? '+' : ''}{((latestPrice - stats.avgPrice) / stats.avgPrice * 100).toFixed(1)}% from average
                    </span>
                  )}
                </div>
                {endTime && (
                  <div className="price-time">
                    <FaClock />
                    <div className="time-info">
                      <span className="time-label">Ends</span>
                      <span className="time-value">{new Date(endTime * 1000).toLocaleDateString('en-US', {month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'})}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="nft-action-buttons">
              {!isConnected ? (
                <div className="connect-message">
                  Connect your wallet to interact with this NFT
                </div>
              ) : (
                <>
                  {/* Regular Buy for Fixed Price */}
                  {isSale && !isAuction && nftOwner === ExchangeAddress && sender !== walletAddress && !isAuctionEnded() && (
                    <button className="action-btn primary" onClick={handleBuy}>
                      <FaShoppingCart /> Buy Now
                    </button>
                  )}

                  {/* Place Bid for Auctions */}
                  {isSale && isAuction && nftOwner === ExchangeAddress && sender !== walletAddress && !isAuctionEnded() && (
                    <button className="action-btn primary" onClick={() => {
                      setModalTitle("Place Bid");
                      setIsModalOpen(true);
                    }}>
                      <FaGavel /> Place Bid
                    </button>
                  )}

                  {/* Claim for Winner after Auction End */}
                  {isSale && isAuction && nftOwner === ExchangeAddress && isAuctionEnded() && isWinner && (
                    <button className="action-btn warning" onClick={handleClaim}>
                      <FaGift /> Claim NFT
                    </button>
                  )}

                  {/* Claim Refund for Loser Bidders after Auction End */}
                  {isSale && isAuction && nftOwner === ExchangeAddress && isAuctionEnded() && hasBidButNotWinner && (
                    <button className="action-btn secondary" onClick={handleClaimRefund}>
                      <FaExchangeAlt /> Claim Refund
                    </button>
                  )}

                  {/* Cancel Listing (before end) */}
                  {isSale && nftOwner === ExchangeAddress && sender === walletAddress && !isAuctionEnded() && (
                    <button className="action-btn danger" onClick={handleCancel}>
                      <FaTimesCircle /> Cancel {isAuction ? 'Auction' : 'Listing'}
                    </button>
                  )}

                  {/* List for Sale */}
                  {!isSale && nftOwner === walletAddress && (
                    <button className="action-btn secondary" onClick={handleSell}>
                      <FaTag /> List for Sale
                    </button>
                  )}

                  {/* Claim NFT Back for Seller (expired, no bids) */}
                  {isClaimableForSeller() && (
                    <button className="action-btn warning" onClick={handleClaim}>
                      <FaGift /> Claim NFT Back
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Tabs */}
            <div className="nft-tabs">
              <button
                className={`tab-item ${activeTab === 'details' ? 'active' : ''}`}
                onClick={() => setActiveTab('details')}
              >
                <FaInfoCircle /> Details
              </button>
              {isAuction && (
                <button
                  className={`tab-item ${activeTab === 'bids' ? 'active' : ''}`}
                  onClick={() => setActiveTab('bids')}
                >
                  <FaGavel /> Bids ({bids.length})
                </button>
              )}
              <button
                className={`tab-item ${activeTab === 'history' ? 'active' : ''}`}
                onClick={() => setActiveTab('history')}
              >
                <FaHistory /> History ({history.length})
              </button>
            </div>

            {/* Tab Content */}
            <div className="nft-tab-content">
              {activeTab === 'details' && (
                <div className="details-tab">
                  {nft.description && (
                    <div className="nft-description-box">
                      <h3>Description</h3>
                      <p>{nft.description}</p>
                    </div>
                  )}
                  
                  {nft.attributes && nft.attributes.length > 0 && (
                    <div className="nft-attributes-box">
                      <h3>Attributes ({nft.attributes.length})</h3>
                      <div className="attributes-list">
                        {nft.attributes.map((attr, index) => (
                          <div key={index} className="attribute-item">
                            <span className="attr-type">{attr.trait_type}</span>
                            <span className="attr-value">{attr.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="token-info-box">
                    <h3>Token Information</h3>
                    <div className="token-info-list">
                      <div className="info-row">
                        <span className="info-label">Collection ID</span>
                        <button 
                          className="info-value copyable"
                          onClick={() => copyToClipboard(collectionId, 'Collection')}
                        >
                          {collectionId}
                          {copied === 'Collection' ? <FaCheckCircle /> : <FaCopy />}
                        </button>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Token Standard</span>
                        <span className="info-value">KDA (Klever NFT)</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">Blockchain</span>
                        <span className="info-value">KleverChain</span>
                      </div>
                      {collection?.royalties && (
                        <div className="info-row">
                          <span className="info-label">Royalties</span>
                          <span className="info-value">{(collection.royalties.percentage / 100).toFixed(2)}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'bids' && isAuction && (
                <div className="bids-tab">
                  {bids && bids.length > 0 ? (
                    <div className="bids-list">
                      {bids.map((bid, index) => (
                        <div key={`${bid.hash}-${index}`} className="bid-item">
                          <div className="bid-rank">#{index + 1}</div>
                          <div className="bid-details">
                            <div className="bid-header">
                              <span className="bid-amount">
                                {bid.amount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 6})} KLV
                              </span>
                              {index === 0 && <span className="highest-bid-badge">Highest Bid</span>}
                            </div>
                            <div className="bid-info">
                              <span className="bid-bidder">
                                <FaUser /> {bid.bidder === walletAddress ? 'You' : `${bid.bidder.slice(0, 8)}...${bid.bidder.slice(-4)}`}
                              </span>
                              <span className="bid-time">
                                <FaClock /> {new Date(bid.timestamp * 1000).toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </span>
                            </div>
                          </div>
                          <a
                            href={`https://kleverscan.org/transaction/${bid.hash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bid-link"
                            title="View transaction"
                          >
                            <FaExternalLinkAlt />
                          </a>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <FaGavel className="empty-icon" />
                      <p>No bids placed yet</p>
                      <span className="empty-hint">Be the first to place a bid!</span>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'history' && (
                <div className="history-tab">
                  {history && history.length > 0 ? (
                    <div className="history-list">
                      {history.map((item, index) => {
                        const { icon: Icon, class: iconClass } = getHistoryIcon(item);
                        return (
                          <div key={index} className="history-entry">
                            <div className="history-icon-box">
                              <Icon className={`history-icon ${iconClass}`} />
                            </div>
                            <div className="history-content">
                              <div className="history-text">
                                {getHistoryText(item)}
                              </div>
                              <div className="history-footer">
                                <span className="history-time">
                                  {formatTimestamp(item.timestamp)}
                                </span>
                                {item.hash && (
                                  <a 
                                    href={`https://kleverscan.org/transaction/${item.hash}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="history-tx-link"
                                  >
                                    View TX <FaExternalLinkAlt />
                                  </a>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="empty-history">
                      <FaHistory size={40} />
                      <p>No transaction history</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={(e) => e.target.className === 'modal-overlay' && closeModal()}>
          <div className="modal-box">
            <div className="modal-header">
              <h2>{modalTitle}</h2>
              <button className="modal-close-btn" onClick={closeModal}>×</button>
            </div>

            <div className="modal-content">
              {modalTitle.includes('Sell') ? (
                <div className="sell-form">
                  {!isPriceConfirmed ? (
                    <>
                      <div className="form-group">
                        <label>Listing Price</label>
                        <div className="price-input-group">
                          <input
                            ref={priceInputRef}
                            type="number"
                            className="form-input"
                            placeholder="0.00"
                            onKeyDown={(e) => e.key === 'Enter' && confirmPrice()}
                            min="0"
                            step="0.000001"
                          />
                          <span className="input-suffix">KLV</span>
                        </div>
                      </div>
                      <button className="set-price-btn" onClick={confirmPrice}>
                        Set Price
                      </button>
                    </>
                  ) : (
                    <div className="price-confirmed">
                      <div className="confirmed-info">
                        <span className="confirmed-label">Listing Price</span>
                        <span className="confirmed-amount">{parseFloat(listingPrice).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 6})} KLV</span>
                      </div>
                      <button className="edit-btn" onClick={() => setIsPriceConfirmed(false)}>
                        Edit
                      </button>
                    </div>
                  )}
                  
                  <div className="form-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      className="form-input"
                      value={endDate}
                      onChange={handleDateChange}
                      min={new Date(Date.now() + 86400000).toISOString().substring(0, 10)}
                    />
                    <span className="form-hint">Listing ends at 11:59 PM on this date</span>
                  </div>
                </div>
              ) : modalTitle.includes('Place Bid') ? (
                <div className="bid-form">
                  <div className="form-group">
                    <label>Your Bid Amount</label>
                    <div className="price-input-group">
                      <input
                        type="number"
                        className="form-input"
                        placeholder={highestBid ? `Min: ${(highestBid.amount + 0.01).toFixed(2)}` : `Min: ${(reservePrice || 0).toFixed(2)}`}
                        value={bidAmount}
                        onChange={(e) => setBidAmount(e.target.value)}
                        step="0.01"
                        min={highestBid ? highestBid.amount + 0.01 : reservePrice || 0}
                      />
                      <span className="currency-label">KLV</span>
                    </div>
                    <div className="bid-info-box">
                      <div className="bid-hint-group">
                        <span className="bid-hint-label">💰 Instant Sell:</span>
                        <span className="bid-hint-value">{reservePrice ? `${reservePrice.toFixed(2)} KLV` : 'N/A'}</span>
                      </div>
                      {highestBid && (
                        <div className="bid-hint-group">
                          <span className="bid-hint-label">🏆 Highest Bid:</span>
                          <span className="bid-hint-value">{highestBid.amount.toFixed(2)} KLV</span>
                        </div>
                      )}
                      <div className="bid-hint-group important">
                        <span className="bid-hint-label">✓ To Outbid:</span>
                        <span className="bid-hint-value">{(highestBid ? `${(highestBid.amount + 0.01).toFixed(2)}+ KLV` : `${(reservePrice || 0).toFixed(2)}+ KLV`)}</span>
                      </div>
                      {bidAmount && parseFloat(bidAmount) > (reservePrice || 0) && !highestBid && (
                        <div className="instant-buy-warning">
                          <FaInfoCircle /> Exceeding reserve = instant purchase!
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : modalTitle.includes('Buy') ? (
                <div className="buy-form">
                  <div className="confirm-preview">
                    {!imageError && nft.image && (
                      <img src={nft.image} alt={nft.name} className="confirm-image" />
                    )}
                    <div className="confirm-info">
                      <strong>{nft.name || `${collectionId} #${nftId}`}</strong>
                      {assetDetails?.isSFT && (
                        <span className="sft-indicator">
                          <FaCube /> Semi-Fungible Token
                        </span>
                      )}
                    </div>
                  </div>

                  {/* SFT Quantity Selector */}
                  {assetDetails?.isSFT && availableQuantity && availableQuantity > 1 && (
                    <div className="sft-quantity-group">
                      <label className="sft-quantity-label">
                        <FaCube /> Quantity to Purchase
                      </label>
                      <div className="sft-quantity-controls">
                        <button
                          type="button"
                          className="sft-quantity-btn"
                          onClick={() => handleBuyQuantityChange(buyQuantity - 1)}
                          disabled={buyQuantity <= 1}
                        >
                          <FaMinus />
                        </button>
                        <input
                          type="number"
                          className="sft-quantity-input"
                          value={buyQuantity}
                          onChange={(e) => handleBuyQuantityChange(e.target.value)}
                          min="1"
                          max={availableQuantity}
                        />
                        <button
                          type="button"
                          className="sft-quantity-btn"
                          onClick={() => handleBuyQuantityChange(buyQuantity + 1)}
                          disabled={buyQuantity >= availableQuantity}
                        >
                          <FaPlus />
                        </button>
                      </div>
                      <span className="sft-quantity-max">Available: {availableQuantity}</span>
                    </div>
                  )}

                  {/* Price Display */}
                  <div className="buy-price-display">
                    {assetDetails?.isSFT ? (
                      <>
                        <div className="price-breakdown">
                          <div className="price-row">
                            <span className="price-label">Price per unit:</span>
                            <span className="price-value">{latestPrice?.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 6})} KLV</span>
                          </div>
                          <div className="price-row">
                            <span className="price-label">Quantity:</span>
                            <span className="price-value">× {buyQuantity}</span>
                          </div>
                          <div className="price-row total">
                            <span className="price-label">Total:</span>
                            <span className="price-value">{((latestPrice || 0) * buyQuantity).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 6})} KLV</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <span className="confirm-price">{latestPrice?.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 6})} KLV</span>
                    )}
                  </div>

                  <p className="confirm-message">
                    {assetDetails?.isSFT
                      ? `Purchase ${buyQuantity} unit${buyQuantity !== 1 ? 's' : ''} for ${((latestPrice || 0) * buyQuantity).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 6})} KLV?`
                      : `Purchase this NFT for ${latestPrice?.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 6})} KLV?`
                    }
                  </p>
                </div>
              ) : (
                <div className="modal-confirm">
                  <div className="confirm-preview">
                    {!imageError && nft.image && (
                      <img src={nft.image} alt={nft.name} className="confirm-image" />
                    )}
                    <div className="confirm-info">
                      <strong>{nft.name || `${collectionId} #${nftId}`}</strong>
                    </div>
                  </div>
                  <p className="confirm-message">
                    {modalTitle.includes('Cancel') && 'Cancel your listing and return this NFT to your wallet?'}
                    {modalTitle.includes('Claim NFT') && 'Claim this NFT after auction end (you are the winner)?'}
                    {modalTitle.includes('Refund') && 'Claim your bid refund after auction end?'}
                  </p>
                </div>
              )}

              {transactionStatus && (
                <div className={`tx-status ${transactionStatus}`}>
                  {transactionStatus === 'processing' && (
                    <>
                      <div className="tx-spinner"></div>
                      <span>Processing...</span>
                    </>
                  )}
                  {transactionStatus === 'success' && (
                    <>
                      <FaCheckCircle />
                      <span>Transaction completed!</span>
                    </>
                  )}
                  {transactionStatus === 'error' && (
                    <>
                      <FaTimesCircle />
                      <span>Transaction failed. Please try again.</span>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button 
                className="modal-btn confirm"
                onClick={handleTransactionConfirmation}
                disabled={transactionStatus === 'processing' || (modalTitle.includes('Sell') && (!isPriceConfirmed || !endTimeUnix))}
              >
                {transactionStatus === 'processing' ? 'Processing...' : 'Confirm'}
              </button>
              <button 
                className="modal-btn cancel"
                onClick={closeModal}
                disabled={transactionStatus === 'processing'}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Auction Listing Modal */}
      {showAuctionModal && nft && (
        <AuctionListingModal
          nft={{
            collectionId: collectionId,
            nftId: nftId,
            name: nft.name || `#${nftId}`,
            image: nft.image
          }}
          onClose={() => setShowAuctionModal(false)}
          onSuccess={() => {
            setShowAuctionModal(false);
            fetchNftData();
          }}
        />
      )}
    </div>
  );
};

export default NftDetailsPage;